import { FormattedMessage } from '@flipster/i18n';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Badge } from '@flipster/react/design-system/badge';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack, vstack } from '@flipster/styled-system/patterns';

import { useAuth } from '../../../../../shared/auth/state';
import { RegularMission } from '../../../types';
import { DistributionDate, MissionStatusBadges, ProgressBar, SectionDescriptionTooltipButton } from '../../misc';
import { MissionCtaButton } from '../../mission-cta-button';
import {
  getDaysUntilExpiration,
  getIsDepositAmountCondition,
  getLocalizedText,
  getMissionStatusFlags,
  getProgressParams,
  isProgressBarType,
} from '../../utils';

interface SelectedMissionPanelProps {
  missions: RegularMission[];
  selectedIndex: number;
  missionTooltipText?: string;
}

const SelectedMissionPanel = ({ missions, selectedIndex, missionTooltipText }: SelectedMissionPanelProps) => {
  const { user } = useAuth();
  const locale = useSupportedLocale();

  const selectedMission = missions[selectedIndex];
  const selectedMissionSpec = selectedMission?.spec;
  const selectedMissionStatus = selectedMission?.status;

  if (!selectedMissionSpec) return null;

  const { isExpired, isRejected, isClaimed, isSettled } = getMissionStatusFlags(selectedMissionStatus);
  const isDisabled = isClaimed || isSettled || isExpired || isRejected;

  const missionTitle = getLocalizedText(selectedMission?.skin?.body?.localizationTable, locale);
  const progressItems = selectedMission?.status?.progress?.filter(isProgressBarType) ?? [];
  const daysUntilExpiration = getDaysUntilExpiration(selectedMissionSpec.validityConditions, user?.createdAt);

  return (
    <div className={css({ px: 32 })}>
      <div className={stack({ backgroundColor: 'background.default.level-0', p: 20, rounded: 8 })}>
        <div className={stack({ minH: 130, justify: 'center', align: 'center' })}>
          <div className={vstack({ gap: 8, textStyle: 'Title/XS' })}>
            <MissionStatusBadges isExpired={isExpired} isRejected={isRejected} />
            <div
              className={center({
                color: isDisabled ? 'content.default.level-1' : 'content.default.level-5',
              })}
            >
              {daysUntilExpiration !== undefined && daysUntilExpiration > 0 && (
                <div className={css({ mr: 8 })}>
                  <Badge type='tint' color='promotion' leadingIcon={<ClockIcon size={16} />}>
                    <FormattedMessage
                      id='Reward_hub.r250213.Reward_hub_0213_days_left'
                      values={{ value: daysUntilExpiration }}
                    />
                  </Badge>
                </div>
              )}
              {missionTitle}
              {missionTooltipText && <SectionDescriptionTooltipButton text={missionTooltipText} />}
            </div>
          </div>
          {isDisabled && selectedMissionStatus?.settleAt && (
            <DistributionDate
              date={selectedMissionStatus?.settleAt}
              showMaintainMessage={selectedMissionSpec.completionConditions.some(getIsDepositAmountCondition)}
              isHide={isExpired || isRejected}
            />
          )}
          {!isDisabled && (
            <div className={hstack({ w: '100%', gap: 48, justify: 'center', mt: 16 })}>
              {progressItems.map((item) => {
                const { target, current } = getProgressParams(item);
                const hasMultiProgress = progressItems.length > 1;
                return (
                  <div key={item.type} className={css({ flex: 1, maxW: 328 })}>
                    {hasMultiProgress && (
                      <p
                        className={css({
                          textStyle: 'Text/M/Strong',
                          color: 'content.default.level-3',
                          mb: 12,
                        })}
                      >
                        {item.type === 'DepositAmount' ? (
                          <FormattedMessage id='Reward_hub.r250213.Deposit' />
                        ) : (
                          <FormattedMessage id='Reward_hub.r250213.Trade' />
                        )}
                      </p>
                    )}
                    <ProgressBar total={target} current={current} />
                    <MissionCtaButton
                      missionIndex={selectedMission?.missionIndex ?? selectedIndex}
                      sectionIndex={selectedMission?.sectionIndex ?? selectedIndex}
                      completionReward={selectedMissionSpec.completionRewards[0]}
                      missionStatus={selectedMissionStatus}
                      progress={item}
                      css={{ mt: 20, w: '100%' }}
                      isActive
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedMissionPanel;
